import React, { useEffect, useState } from 'react';
import { Paintbrush, Droplets, Calendar, Clock, MapPin, CreditCard, ChevronRight, User, Mail, Phone, LogIn, UserPlus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { useAuth } from '../../components/auth/AuthContext';
type ServiceRequest = {
  serviceType: string;
  serviceDetails: any;
  schedule: {
    date: string;
    time: string;
  };
  location: string;
  paymentMethod: string;
  contactInfo: {
    name: string;
    email: string;
    phone: string;
  };
};
export function PublicRequestService() {
  const {
    isAuthenticated,
    isCustomer
  } = useAuth();
  const navigate = useNavigate();
  // Require authentication to access the service request form
  if (!isAuthenticated || !isCustomer) {
    return <div className="min-h-screen flex flex-col bg-background light" data-id="element-1444">
        <Navbar data-id="element-1445" />
        <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex items-center justify-center" data-id="element-1446">
          <div className="max-w-md mx-auto text-center bg-white p-8 rounded-lg shadow-sm border border-gray-200" data-id="element-1447">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-1448">
              <LogIn className="h-8 w-8 text-primary" data-id="element-1449" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1450">
              Login Required
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-1451">
              Please log in or create an account to request a service.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1452">
              <Link to="/auth/customer-login" className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors" data-id="element-1453">
                <div className="flex items-center justify-center" data-id="element-1454">
                  <LogIn className="h-5 w-5 mr-2" data-id="element-1455" />
                  Login
                </div>
              </Link>
              <Link to="/auth/customer-register" className="px-6 py-3 border border-primary text-primary rounded-md hover:bg-primary/10 transition-colors" data-id="element-1456">
                <div className="flex items-center justify-center" data-id="element-1457">
                  <UserPlus className="h-5 w-5 mr-2" data-id="element-1458" />
                  Create Account
                </div>
              </Link>
            </div>
          </div>
        </main>
        <Footer data-id="element-1459" />
      </div>;
  }
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [serviceRequest, setServiceRequest] = useState<ServiceRequest>({
    serviceType: '',
    serviceDetails: {},
    schedule: {
      date: '',
      time: ''
    },
    location: '',
    paymentMethod: 'full',
    contactInfo: {
      name: '',
      email: '',
      phone: ''
    }
  });
  const services = [{
    id: 'painting',
    name: 'Painting Services',
    description: 'Professional painting with eco-friendly 0 VOC paints',
    icon: <Paintbrush className="h-8 w-8 text-primary" data-id="element-1460" />
  }, {
    id: 'waterproofing',
    name: 'Waterproofing',
    description: 'Complete moisture protection solutions for your home',
    icon: <Droplets className="h-8 w-8 text-primary" data-id="element-1461" />
  }];
  const handleServiceSelect = (serviceId: string) => {
    setSelectedService(serviceId);
    setServiceRequest(prev => ({
      ...prev,
      serviceType: serviceId
    }));
    setStep(2);
  };
  const handleServiceDetailsChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setServiceRequest(prev => ({
      ...prev,
      serviceDetails: {
        ...prev.serviceDetails,
        [name]: value
      }
    }));
  };
  const handleScheduleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setServiceRequest(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [name]: value
      }
    }));
  };
  const handleLocationChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setServiceRequest(prev => ({
      ...prev,
      location: e.target.value
    }));
  };
  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setServiceRequest(prev => ({
      ...prev,
      paymentMethod: e.target.value
    }));
  };
  const handleContactInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setServiceRequest(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [name]: value
      }
    }));
  };
  const handleNextStep = () => {
    if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      // User is authenticated, proceed directly to payment
      navigate('/customer/payment', {
        state: {
          serviceRequest
        }
      });
    }
  };
  const handleSubmitToLogin = () => {
    // Save service request to localStorage and redirect to login
    localStorage.setItem('pendingServiceRequest', JSON.stringify(serviceRequest));
    navigate('/auth/customer-login?redirect=service-request');
  };
  const handleSubmitToRegister = () => {
    // Save service request to localStorage and redirect to registration
    localStorage.setItem('pendingServiceRequest', JSON.stringify(serviceRequest));
    navigate('/auth/customer-register?redirect=service-request');
  };
  // Render step 4 (login required) only when user tries to submit
  const renderStepFour = () => <div className="max-w-md mx-auto text-center bg-white p-8 rounded-lg shadow-sm border border-gray-200" data-id="element-1462">
      <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-1463">
        <LogIn className="h-8 w-8 text-primary" data-id="element-1464" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1465">Almost There!</h2>
      <p className="text-gray-600 mb-8" data-id="element-1466">
        Please log in or create an account to complete your service request.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1467">
        <button onClick={handleSubmitToLogin} className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors" data-id="element-1468">
          <div className="flex items-center justify-center" data-id="element-1469">
            <LogIn className="h-5 w-5 mr-2" data-id="element-1470" />
            Login
          </div>
        </button>
        <button onClick={handleSubmitToRegister} className="px-6 py-3 border border-primary text-primary rounded-md hover:bg-primary/10 transition-colors" data-id="element-1471">
          <div className="flex items-center justify-center" data-id="element-1472">
            <UserPlus className="h-5 w-5 mr-2" data-id="element-1473" />
            Create Account
          </div>
        </button>
      </div>
    </div>;
  const renderStepOne = () => <div data-id="element-1474">
      <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1475">Select a Service</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto" data-id="element-1476">
        {services.map(service => <div key={service.id} className={`border rounded-lg p-6 cursor-pointer transition-all ${selectedService === service.id ? 'border-primary bg-primary/5 shadow-md' : 'border-gray-200 hover:border-primary/50 hover:shadow'}`} onClick={() => handleServiceSelect(service.id)} data-id="element-1477">
            <div className="flex flex-col items-center text-center" data-id="element-1478">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4" data-id="element-1479">
                {service.icon}
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1480">
                {service.name}
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1481">{service.description}</p>
            </div>
          </div>)}
      </div>
    </div>;
  const renderStepTwo = () => {
    // Different form fields based on selected service
    let serviceOptions = null;
    if (selectedService === 'painting') {
      serviceOptions = <div className="space-y-4" data-id="element-1482">
          <div data-id="element-1483">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1484">
              Area to Paint
            </label>
            <select name="area" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" onChange={handleServiceDetailsChange} value={serviceRequest.serviceDetails.area || ''} data-id="element-1485">
              <option value="" data-id="element-1486">Select area</option>
              <option value="living_room" data-id="element-1487">Living Room</option>
              <option value="bedroom" data-id="element-1488">Bedroom</option>
              <option value="kitchen" data-id="element-1489">Kitchen</option>
              <option value="bathroom" data-id="element-1490">Bathroom</option>
              <option value="exterior" data-id="element-1491">Exterior</option>
              <option value="full_house" data-id="element-1492">Full House</option>
            </select>
          </div>
          <div data-id="element-1493">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1494">
              Paint Type
            </label>
            <select name="paintType" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" onChange={handleServiceDetailsChange} value={serviceRequest.serviceDetails.paintType || ''} data-id="element-1495">
              <option value="" data-id="element-1496">Select paint type</option>
              <option value="green_home" data-id="element-1497">Green Home 0 VOC</option>
              <option value="premium" data-id="element-1498">Premium Finish</option>
              <option value="standard" data-id="element-1499">Standard</option>
            </select>
          </div>
          <div data-id="element-1500">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1501">
              Wall Condition
            </label>
            <select name="wallCondition" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" onChange={handleServiceDetailsChange} value={serviceRequest.serviceDetails.wallCondition || ''} data-id="element-1502">
              <option value="" data-id="element-1503">Select condition</option>
              <option value="good" data-id="element-1504">Good - Just needs repainting</option>
              <option value="minor_repair" data-id="element-1505">Needs minor repairs</option>
              <option value="major_repair" data-id="element-1506">Needs major repairs</option>
            </select>
          </div>
          <div data-id="element-1507">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1508">
              Additional Notes
            </label>
            <textarea name="notes" rows={3} className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="Any specific requirements or details" onChange={handleServiceDetailsChange} value={serviceRequest.serviceDetails.notes || ''} data-id="element-1509"></textarea>
          </div>
        </div>;
    } else if (selectedService === 'waterproofing') {
      serviceOptions = <div className="space-y-4" data-id="element-1510">
          <div data-id="element-1511">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1512">
              Area to Waterproof
            </label>
            <select name="area" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" onChange={handleServiceDetailsChange} value={serviceRequest.serviceDetails.area || ''} data-id="element-1513">
              <option value="" data-id="element-1514">Select area</option>
              <option value="bathroom" data-id="element-1515">Bathroom</option>
              <option value="roof" data-id="element-1516">Roof</option>
              <option value="balcony" data-id="element-1517">Balcony</option>
              <option value="exterior_wall" data-id="element-1518">Exterior Wall</option>
              <option value="basement" data-id="element-1519">Basement</option>
            </select>
          </div>
          <div data-id="element-1520">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1521">
              Current Condition
            </label>
            <select name="condition" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" onChange={handleServiceDetailsChange} value={serviceRequest.serviceDetails.condition || ''} data-id="element-1522">
              <option value="" data-id="element-1523">Select condition</option>
              <option value="preventive" data-id="element-1524">Preventive - No current leaks</option>
              <option value="minor_leak" data-id="element-1525">Minor leaks present</option>
              <option value="major_leak" data-id="element-1526">Major leaks/water damage</option>
            </select>
          </div>
          <div data-id="element-1527">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1528">
              Area Size (approx. in sq. ft)
            </label>
            <input name="size" type="number" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="e.g. 100" onChange={handleServiceDetailsChange} value={serviceRequest.serviceDetails.size || ''} data-id="element-1529" />
          </div>
          <div data-id="element-1530">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1531">
              Additional Notes
            </label>
            <textarea name="notes" rows={3} className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="Any specific requirements or details" onChange={handleServiceDetailsChange} value={serviceRequest.serviceDetails.notes || ''} data-id="element-1532"></textarea>
          </div>
        </div>;
    }
    return <div data-id="element-1533">
        <button onClick={() => setStep(1)} className="flex items-center text-primary mb-4 hover:underline" data-id="element-1534">
          <ChevronRight className="h-4 w-4 mr-1 rotate-180" data-id="element-1535" />
          Back to services
        </button>
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1536">
          {selectedService === 'painting' && 'Request Painting Service'}
          {selectedService === 'waterproofing' && 'Request Waterproofing Service'}
        </h2>
        <div className="bg-white rounded-lg shadow p-6" data-id="element-1537">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1538">
            <div data-id="element-1539">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1540">
                Service Details
              </h3>
              {serviceOptions}
            </div>
            <div data-id="element-1541">
              <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1542">
                Schedule & Location
              </h3>
              <div className="space-y-4" data-id="element-1543">
                <div data-id="element-1544">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1545">
                    Preferred Date
                  </label>
                  <div className="flex items-center" data-id="element-1546">
                    <Calendar className="h-5 w-5 text-gray-400 mr-2" data-id="element-1547" />
                    <input type="date" name="date" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" onChange={handleScheduleChange} value={serviceRequest.schedule.date} data-id="element-1548" />
                  </div>
                </div>
                <div data-id="element-1549">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1550">
                    Preferred Time
                  </label>
                  <div className="flex items-center" data-id="element-1551">
                    <Clock className="h-5 w-5 text-gray-400 mr-2" data-id="element-1552" />
                    <select name="time" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" onChange={handleScheduleChange} value={serviceRequest.schedule.time} data-id="element-1553">
                      <option value="" data-id="element-1554">Select time</option>
                      <option value="morning" data-id="element-1555">
                        Morning (8:00 AM - 12:00 PM)
                      </option>
                      <option value="afternoon" data-id="element-1556">
                        Afternoon (12:00 PM - 4:00 PM)
                      </option>
                      <option value="evening" data-id="element-1557">
                        Evening (4:00 PM - 8:00 PM)
                      </option>
                    </select>
                  </div>
                </div>
                <div data-id="element-1558">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1559">
                    Service Address
                  </label>
                  <div className="flex items-start" data-id="element-1560">
                    <MapPin className="h-5 w-5 text-gray-400 mr-2 mt-1" data-id="element-1561" />
                    <textarea rows={3} className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="Enter your full address" onChange={handleLocationChange} value={serviceRequest.location} data-id="element-1562"></textarea>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-200" data-id="element-1563">
                  <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1564">
                    Payment Method
                  </h3>
                  <div className="space-y-2" data-id="element-1565">
                    <label className="flex items-center" data-id="element-1566">
                      <input type="radio" name="payment" value="full" className="h-4 w-4 text-primary focus:ring-primary border-gray-300" checked={serviceRequest.paymentMethod === 'full'} onChange={handlePaymentMethodChange} data-id="element-1567" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-1568">
                        Pay in full
                      </span>
                    </label>
                    <label className="flex items-center" data-id="element-1569">
                      <input type="radio" name="payment" value="installment" className="h-4 w-4 text-primary focus:ring-primary border-gray-300" checked={serviceRequest.paymentMethod === 'installment'} onChange={handlePaymentMethodChange} data-id="element-1570" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-1571">
                        3-month 0% installment
                      </span>
                    </label>
                  </div>
                  <div className="mt-4 flex items-center text-sm text-gray-500" data-id="element-1572">
                    <CreditCard className="h-5 w-5 text-gray-400 mr-2" data-id="element-1573" />
                    <span data-id="element-1574">
                      You'll be asked to provide payment details after your
                      request is approved
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 flex justify-end" data-id="element-1575">
            <button onClick={handleNextStep} className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90" data-id="element-1576">
              Continue to Contact Info
            </button>
          </div>
        </div>
      </div>;
  };
  const renderStepThree = () => {
    return <div data-id="element-1577">
        <button onClick={() => setStep(2)} className="flex items-center text-primary mb-4 hover:underline" data-id="element-1578">
          <ChevronRight className="h-4 w-4 mr-1 rotate-180" data-id="element-1579" />
          Back to service details
        </button>
        <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1580">
          Contact Information
        </h2>
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto" data-id="element-1581">
          <p className="text-gray-600 mb-6" data-id="element-1582">
            Please provide your contact details so we can get in touch about
            your service request.
          </p>
          <div className="space-y-4" data-id="element-1583">
            <div data-id="element-1584">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1585">
                Full Name
              </label>
              <div className="flex items-center" data-id="element-1586">
                <User className="h-5 w-5 text-gray-400 mr-2" data-id="element-1587" />
                <input type="text" name="name" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="Your full name" value={serviceRequest.contactInfo.name} onChange={handleContactInfoChange} data-id="element-1588" />
              </div>
            </div>
            <div data-id="element-1589">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1590">
                Email Address
              </label>
              <div className="flex items-center" data-id="element-1591">
                <Mail className="h-5 w-5 text-gray-400 mr-2" data-id="element-1592" />
                <input type="email" name="email" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="Your email address" value={serviceRequest.contactInfo.email} onChange={handleContactInfoChange} data-id="element-1593" />
              </div>
            </div>
            <div data-id="element-1594">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1595">
                Phone Number
              </label>
              <div className="flex items-center" data-id="element-1596">
                <Phone className="h-5 w-5 text-gray-400 mr-2" data-id="element-1597" />
                <input type="tel" name="phone" className="w-full border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring-primary" placeholder="Your phone number" value={serviceRequest.contactInfo.phone} onChange={handleContactInfoChange} data-id="element-1598" />
              </div>
            </div>
          </div>
          <div className="mt-8 flex justify-end" data-id="element-1599">
            <button onClick={handleNextStep} className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90" data-id="element-1600">
              Continue to Account
            </button>
          </div>
        </div>
      </div>;
  };
  return <div className="min-h-screen flex flex-col bg-background light" data-id="element-1601">
      <Navbar data-id="element-1602" />
      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1603">
        <div className="mb-8" data-id="element-1604">
          <div className="flex items-center max-w-3xl mx-auto" data-id="element-1605">
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-1606">
              1
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} data-id="element-1607"></div>
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-1608">
              2
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-primary' : 'bg-gray-200'}`} data-id="element-1609"></div>
            <div className={`flex items-center justify-center h-8 w-8 rounded-full ${step >= 3 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-1610">
              3
            </div>
          </div>
          <div className="flex justify-between mt-2 text-sm max-w-3xl mx-auto px-2" data-id="element-1611">
            <div className={step >= 1 ? 'text-primary font-medium' : 'text-gray-500'} data-id="element-1612">
              Select Service
            </div>
            <div className={step >= 2 ? 'text-primary font-medium' : 'text-gray-500'} data-id="element-1613">
              Service Details
            </div>
            <div className={step >= 3 ? 'text-primary font-medium' : 'text-gray-500'} data-id="element-1614">
              Review & Submit
            </div>
          </div>
        </div>
        {step === 1 && renderStepOne()}
        {step === 2 && renderStepTwo()}
        {step === 3 && renderStepThree()}
      </main>
      <Footer data-id="element-1615" />
    </div>;
}